import { clsx } from 'clsx'
import { useEffect, useRef, useState } from 'react'
import { isComposing } from '@/lib/ime'

type Props = {
  value: string
  editing: boolean
  onEditingChange: (editing: boolean) => void
  onCommit: (next: string) => void
  maxLength: number
  className?: string
  inputClassName?: string
  /** Size the input to its text instead of filling the container. */
  autosize?: boolean
  placeholder?: string
  /** The input's accessible name (what is being renamed). */
  label?: string
}

/** Inline-editable text. Enter commits, Esc cancels; both ignore IME composition. */
export function EditableTitle({ editing, value, className, ...rest }: Props) {
  // `whitespace-pre` so the text shows exactly as typed (repeated spaces included),
  // matching the input when editing.
  if (!editing) return <span className={clsx('whitespace-pre', className)}>{value}</span>
  return <TitleInput value={value} {...rest} />
}

function TitleInput({
  value,
  onEditingChange,
  onCommit,
  maxLength,
  inputClassName,
  autosize,
  placeholder,
  label,
}: Omit<Props, 'editing' | 'className'>) {
  const [draft, setDraft] = useState(value)
  const input = useRef<HTMLInputElement>(null)

  useEffect(() => {
    input.current?.select()
  }, [])

  const commit = () => {
    const next = draft.trim()
    onEditingChange(false)
    if (next && next !== value) onCommit(next)
  }

  const field = (
    <input
      ref={input}
      value={draft}
      maxLength={maxLength}
      size={1}
      {...(placeholder && { placeholder })}
      {...(label && { 'aria-label': label })}
      onChange={(e) => {
        setDraft(e.target.value)
      }}
      onBlur={commit}
      onClick={(e) => {
        e.stopPropagation()
      }}
      onKeyDown={(e) => {
        e.stopPropagation()
        if (isComposing(e)) return
        if (e.key === 'Enter') {
          e.preventDefault()
          commit()
        } else if (e.key === 'Escape') {
          e.preventDefault()
          onEditingChange(false)
        }
      }}
      className={clsx(
        'rounded-sm bg-surface px-1 outline-2 outline-(--focus)',
        autosize ? 'col-start-1 row-start-1 w-full min-w-0' : '-mx-1 w-full',
        inputClassName,
      )}
    />
  )

  if (!autosize) return field
  // An invisible copy of the text shares the input's grid cell and sets its width.
  return (
    <span className="-mx-1 inline-grid max-w-full align-baseline">
      <span
        aria-hidden="true"
        className={clsx('invisible col-start-1 row-start-1 px-1 whitespace-pre', inputClassName)}
      >
        {(draft || placeholder || ' ') + ' '}
      </span>
      {field}
    </span>
  )
}
